import { z } from 'zod'
import { Model } from './architecture'
import type { Market, Markets } from '../shared/domain/Market'
import { DomainError } from './DomainError'
import { ErrorsCode } from './ErrorsCode'
import { COUNTRIES, countryDataSource } from '../_datasource/CountryDatasource'

export type Countries = (typeof ALLOWED_COUNTRIES)[number]

export const ALLOWED_COUNTRIES = COUNTRIES

const FROM_MARKET_TO_COUNTRY: Record<Markets, Countries> = Object.values(countryDataSource).reduce(
  (acc, config) => {
    if (!acc[config.market]) {
      acc[config.market] = config.countryCode
    }
    return acc
  },
  {} as Record<Markets, Countries>
)

export function isCountryType(value: string): value is Countries {
  return ALLOWED_COUNTRIES.includes(value as Countries)
}

export const CountryValidation = z.object({
  value: z.enum(ALLOWED_COUNTRIES, { required_error: '[Country] value is required' }),
})

export class Country extends Model {
  static create({ value }: z.infer<typeof CountryValidation>) {
    CountryValidation.parse({ value })
    return new Country(value)
  }

  static empty() {
    return new Country('us')
  }

  static fromMarket({ market }: { market: Market }) {
    if (!market.value) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    return Country.create({ value: FROM_MARKET_TO_COUNTRY[market.value] })
  }

  constructor(private readonly _value: Countries) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  toJSON() {
    return { value: this.value }
  }
}
